// frontend/src/components/Edi/EdiClientConfig.jsx
//
// Follows the same conventions as your other settings screens:
// DataTable for the list, SearchableSelect for the protocol dropdown,
// usePermissions().can(menuKey, op) for gating actions, toast_error/
// toast_success for feedback. Adjust import paths below to match your
// actual folder structure if they differ.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '../../context/TranslationContext';
import { FiPlus, FiEdit2, FiTrash2, FiRefreshCw, FiX, FiMapPin } from 'react-icons/fi';
import DataTable from '../Common/DataTable';
import SearchableSelect from '../Common/SearchableSelect';
import Toast from '../Common/Toast';
import { useToast } from '../../hooks/useToast';
import { usePermissions } from '../../context/PermissionsContext';
import { ediAPI } from '../../api/client';
import './EdiClientConfig.css';

const MENU_KEY = 'edi_client_config';
const POLL_MS = 3000;

const PROTOCOL_OPTIONS = [
  { value: 'SFTP', label: 'SFTP' },
  { value: 'FTP', label: 'FTP' },
  { value: 'FTPS', label: 'FTPS' },
];

const EMPTY_FORM = {
  client_name: '',
  protocol: 'SFTP',
  host: '',
  port: 22,
  username: '',
  password: '',
  remote_folder: '',
  active: true,
};

const EMPTY_GLN_FORM = {
  store_gln: '',
  store_name: '',
  store_address: '',
  store_city: '',
  erp_warehouse: '',
  active: true,
};

const EdiClientConfig = () => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { toasts, removeToast, toast_success, toast_error } = useToast();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // GLN / stores modal
  const [glnClient, setGlnClient] = useState(null);
  const [glns, setGlns] = useState([]);
  const [glnLoading, setGlnLoading] = useState(false);
  const [glnForm, setGlnForm] = useState(EMPTY_GLN_FORM);
  const [glnEditingId, setGlnEditingId] = useState(null);
  const [glnSaving, setGlnSaving] = useState(false);

  const canWrite = can(MENU_KEY, 'write');
  const canDelete = can(MENU_KEY, 'delete');
  const canCreate = can(MENU_KEY, 'create');

  // clients that were running at the previous poll — used to detect completion
  const prevRunningRef = useRef(new Set());

  const loadClients = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const res = await ediAPI.getClients();
      setClients(res.data.clients || []);
      setError(null);
    } catch (err) {
      if (!silent) {
        setError(err.response?.data?.error || t('edi.load_error', 'Erro ao carregar configurações EDI.'));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadClients(); }, [loadClients]);

  // The import runs on the server, so it survives closing the page. While any
  // client is running we poll; on (re)opening the page a run still in progress
  // shows up immediately as "A processar…", and its completion is announced.
  const anyRunning = clients.some((c) => c.running);

  useEffect(() => {
    if (!anyRunning) return undefined;
    const timer = setInterval(() => loadClients({ silent: true }), POLL_MS);
    return () => clearInterval(timer);
  }, [anyRunning, loadClients]);

  useEffect(() => {
    const nowRunning = new Set(clients.filter((c) => c.running).map((c) => c.id));
    for (const c of clients) {
      if (prevRunningRef.current.has(c.id) && !c.running) {
        const last = c.last_run;
        if (last && last.ok === false) {
          toast_error(`${c.client_name}: ${last.error || t('edi.retrieve_error', 'Erro ao processar mensagens EDI.')}`);
        } else if (last && last.processed > 0) {
          toast_success(`${c.client_name}: ${t('edi.retrieve_success_files', `Processados ${last.processed} ficheiro(s).`)}`);
        } else {
          toast_success(`${c.client_name}: ${t('edi.retrieve_success_none', 'Sem novos ficheiros para processar.')}`);
        }
      }
    }
    prevRunningRef.current = nowRunning;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  const openCreateModal = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEditModal = (client) => {
    setEditingId(client.id);
    setForm({
      client_name: client.client_name,
      protocol: client.protocol,
      host: client.host,
      port: client.port,
      username: client.username,
      password: '', // left blank — only sent if the user types a new one
      remote_folder: client.remote_folder || '',
      active: client.active,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.client_name || !form.host || !form.username) {
      toast_error(t('edi.required_fields', 'Preencha nome, host e utilizador.'));
      return;
    }
    if (!editingId && !form.password) {
      toast_error(t('edi.password_required', 'A password é obrigatória ao criar uma nova configuração.'));
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        await ediAPI.updateClient(editingId, form);
        toast_success(t('edi.updated', 'Configuração EDI atualizada.'));
      } else {
        await ediAPI.createClient(form);
        toast_success(t('edi.created', 'Configuração EDI criada.'));
      }
      closeModal();
      loadClients();
    } catch (err) {
      toast_error(err.response?.data?.error || t('edi.save_error', 'Erro ao guardar configuração EDI.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(t('edi.confirm_delete', `Remover a configuração "${client.client_name}"?`))) return;
    try {
      await ediAPI.deleteClient(client.id);
      toast_success(t('edi.deleted', 'Configuração EDI removida.'));
      loadClients();
    } catch (err) {
      toast_error(err.response?.data?.error || t('edi.delete_error', 'Erro ao remover configuração EDI.'));
    }
  };

  // Starts the import in the background (server answers 202 immediately).
  const handleRetrieveNow = async (client) => {
    try {
      await ediAPI.retrieveNow(client.id);
      toast_success(t('edi.retrieve_started', 'Importação iniciada. Continua em segundo plano, mesmo que feche esta página.'));
    } catch (err) {
      if (err.response?.status === 409) {
        toast_error(t('edi.already_running', 'Já existe um processamento em curso para este cliente.'));
      } else {
        toast_error(err.response?.data?.error || t('edi.retrieve_error', 'Erro ao processar mensagens EDI.'));
      }
    } finally {
      // picks up running=true, which also starts the polling
      loadClients({ silent: true });
    }
  };

  // ---------- GLN / stores ----------

  const loadGlns = useCallback(async (clientId) => {
    try {
      setGlnLoading(true);
      const res = await ediAPI.getClientGlns(clientId);
      setGlns(res.data.glns || []);
    } catch (err) {
      toast_error(err.response?.data?.error || t('edi.gln_load_error', 'Erro ao carregar GLNs.'));
    } finally {
      setGlnLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openGlnModal = (client) => {
    setGlnClient(client);
    setGlns([]);
    setGlnForm(EMPTY_GLN_FORM);
    setGlnEditingId(null);
    loadGlns(client.id);
  };

  const closeGlnModal = () => {
    setGlnClient(null);
    setGlns([]);
    setGlnForm(EMPTY_GLN_FORM);
    setGlnEditingId(null);
  };

  const handleGlnField = (field, value) => {
    setGlnForm((prev) => ({ ...prev, [field]: value }));
  };

  const startEditGln = (g) => {
    setGlnEditingId(g.id);
    setGlnForm({
      store_gln: g.store_gln || '',
      store_name: g.store_name || '',
      store_address: g.store_address || '',
      store_city: g.store_city || '',
      erp_warehouse: g.erp_warehouse || '',
      active: g.active !== false,
    });
  };

  const cancelEditGln = () => {
    setGlnEditingId(null);
    setGlnForm(EMPTY_GLN_FORM);
  };

  const handleSaveGln = async () => {
    if (!glnForm.store_gln.trim()) {
      toast_error(t('edi.gln_required', 'O GLN da loja é obrigatório.'));
      return;
    }
    try {
      setGlnSaving(true);
      if (glnEditingId) {
        await ediAPI.updateClientGln(glnEditingId, glnForm);
        toast_success(t('edi.gln_updated', 'GLN atualizado.'));
      } else {
        await ediAPI.createClientGln(glnClient.id, glnForm);
        toast_success(t('edi.gln_created', 'GLN criado.'));
      }
      cancelEditGln();
      loadGlns(glnClient.id);
    } catch (err) {
      toast_error(err.response?.data?.error || t('edi.gln_save_error', 'Erro ao guardar GLN.'));
    } finally {
      setGlnSaving(false);
    }
  };

  const handleDeleteGln = async (g) => {
    if (!window.confirm(t('edi.gln_confirm_delete', `Remover o GLN "${g.store_gln}"?`))) return;
    try {
      await ediAPI.deleteClientGln(g.id);
      toast_success(t('edi.gln_deleted', 'GLN removido.'));
      if (glnEditingId === g.id) cancelEditGln();
      loadGlns(glnClient.id);
    } catch (err) {
      toast_error(err.response?.data?.error || t('edi.gln_delete_error', 'Erro ao remover GLN.'));
    }
  };

  const columns = [
    { header: t('edi.client_name', 'Cliente'), field: 'client_name' },
    { header: t('edi.protocol', 'Protocolo'), field: 'protocol', className: 'col-narrow' },
    { header: t('edi.host', 'Host'), field: 'host' },
    {
      header: t('edi.status', 'Estado'),
      field: 'active',
      className: 'col-narrow',
      render: (row) => (
        <>
          <span className={`edi-status-badge ${row.active ? 'active' : 'inactive'}`}>
            {row.active ? t('edi.active', 'Ativo') : t('edi.inactive', 'Inativo')}
          </span>
          {row.running && (
            <span className="edi-status-badge processing">
              <FiRefreshCw size={11} className="spin" /> {t('edi.processing', 'A processar…')}
            </span>
          )}
        </>
      ),
    },
    {
      header: t('actions', 'Ações'),
      field: 'actions',
      className: 'col-actions',
      render: (row) => (
        <div className="edi-row-actions">
          {canWrite && (
            <button
              className="btn-icon"
              title={row.running ? t('edi.processing', 'A processar…') : t('edi.retrieve_now', 'Obter agora')}
              disabled={row.running}
              onClick={() => handleRetrieveNow(row)}
            >
              <FiRefreshCw className={row.running ? 'spin' : ''} />
            </button>
          )}
          <button
            className="btn-icon"
            title={t('edi.stores_glns', 'Lojas / GLNs')}
            onClick={() => openGlnModal(row)}
          >
            <FiMapPin />
          </button>
          {canWrite && (
            <button className="btn-icon" title={t('edit', 'Editar')} onClick={() => openEditModal(row)}>
              <FiEdit2 />
            </button>
          )}
          {canDelete && (
            <button className="btn-icon btn-icon-danger" title={t('delete', 'Eliminar')} onClick={() => handleDelete(row)}>
              <FiTrash2 />
            </button>
          )}
        </div>
      ),
    },
  ];

  const glnColumns = [
    { header: t('edi.store_gln', 'GLN'), field: 'store_gln' },
    { header: t('edi.store_name', 'Loja'), field: 'store_name' },
    { header: t('edi.store_address', 'Morada'), field: 'store_address' },
    { header: t('edi.store_city', 'Cidade'), field: 'store_city' },
    { header: t('edi.erp_warehouse', 'Armazém ERP'), field: 'erp_warehouse' },
    {
      header: t('edi.status', 'Estado'),
      field: 'active',
      className: 'col-narrow',
      render: (row) => (
        <span className={`edi-status-badge ${row.active ? 'active' : 'inactive'}`}>
          {row.active ? t('edi.active', 'Ativo') : t('edi.inactive', 'Inativo')}
        </span>
      ),
    },
    {
      header: t('actions', 'Ações'),
      field: 'actions',
      className: 'col-actions-sm',
      render: (row) => (
        <div className="edi-row-actions">
          {canWrite && (
            <button className="btn-icon" title={t('edit', 'Editar')} onClick={() => startEditGln(row)}>
              <FiEdit2 />
            </button>
          )}
          {canDelete && (
            <button className="btn-icon btn-icon-danger" title={t('delete', 'Eliminar')} onClick={() => handleDeleteGln(row)}>
              <FiTrash2 />
            </button>
          )}
        </div>
      ),
    },
  ];

  const canEditGlnForm = glnEditingId ? canWrite : canCreate;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{t('edi.title', 'Configuração EDI')}</h1>
        <p className="page-subtitle">
          {t('edi.subtitle', 'Configure o acesso SFTP por cliente para receção de mensagens INVRPT e SLSRPT.')}
        </p>
        {canCreate && (
          <button className="btn-primary" onClick={openCreateModal}>
            <FiPlus /> {t('edi.new_client', 'Nova configuração')}
          </button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={clients}
        loading={loading}
        error={error}
        onRetry={loadClients}
        emptyMessage={t('edi.no_clients', 'Nenhuma configuração EDI encontrada.')}
      />

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content edi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? t('edi.edit_client', 'Editar configuração EDI') : t('edi.new_client', 'Nova configuração EDI')}</h2>
              <button className="btn-icon" onClick={closeModal}><FiX /></button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>{t('edi.client_name', 'Cliente')} *</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.client_name}
                  onChange={(e) => handleFieldChange('client_name', e.target.value)}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('edi.protocol', 'Protocolo')}</label>
                  <SearchableSelect
                    value={form.protocol}
                    onChange={(v) => handleFieldChange('protocol', v)}
                    options={PROTOCOL_OPTIONS}
                    getOptionLabel={(opt) => opt.label}
                    getOptionValue={(opt) => opt.value}
                  />
                </div>
                <div className="form-group form-group-sm">
                  <label>{t('edi.port', 'Porta')}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.port}
                    onChange={(e) => handleFieldChange('port', parseInt(e.target.value, 10) || '')}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('edi.host', 'Host')} *</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.host}
                  onChange={(e) => handleFieldChange('host', e.target.value)}
                  placeholder="sftp.cliente.com"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('edi.username', 'Utilizador')} *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.username}
                    onChange={(e) => handleFieldChange('username', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>
                    {t('edi.password', 'Password')}
                    {editingId ? ` (${t('edi.leave_blank', 'deixar em branco para manter')})` : ' *'}
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    value={form.password}
                    onChange={(e) => handleFieldChange('password', e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('edi.folder', 'Pasta remota')}</label>
                <input
                  type="text"
                  className="form-control"
                  value={form.remote_folder}
                  onChange={(e) => handleFieldChange('remote_folder', e.target.value)}
                  placeholder="/edi"
                />
                <small className="form-hint">
                  {t('edi.folder_hint', 'Pasta partilhada — o tipo de mensagem (INVRPT/SLSRPT) é detetado automaticamente no conteúdo de cada ficheiro.')}
                </small>
              </div>

              <div className="form-group form-group-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => handleFieldChange('active', e.target.checked)}
                  />
                  {t('edi.active', 'Ativo')}
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal} disabled={saving}>
                {t('cancel', 'Cancelar')}
              </button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? t('saving', 'A guardar...') : t('save', 'Guardar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {glnClient && (
        <div className="modal-overlay" onClick={closeGlnModal}>
          <div className="modal-content edi-modal edi-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('edi.stores_glns', 'Lojas / GLNs')} — {glnClient.client_name}</h2>
              <button className="btn-icon" onClick={closeGlnModal}><FiX /></button>
            </div>

            <div className="modal-body">
              {(canCreate || (glnEditingId && canWrite)) && (
                <div className="edi-gln-form">
                  <h3>
                    {glnEditingId
                      ? t('edi.gln_edit', 'Editar GLN')
                      : t('edi.gln_new', 'Novo GLN')}
                  </h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('edi.store_gln', 'GLN')} *</label>
                      <input
                        type="text"
                        className="form-control"
                        maxLength={20}
                        value={glnForm.store_gln}
                        onChange={(e) => handleGlnField('store_gln', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('edi.store_name', 'Loja')}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={glnForm.store_name}
                        onChange={(e) => handleGlnField('store_name', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('edi.store_address', 'Morada')}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={glnForm.store_address}
                      onChange={(e) => handleGlnField('store_address', e.target.value)}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('edi.store_city', 'Cidade')}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={glnForm.store_city}
                        onChange={(e) => handleGlnField('store_city', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('edi.erp_warehouse', 'Armazém ERP')}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={glnForm.erp_warehouse}
                        onChange={(e) => handleGlnField('erp_warehouse', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="edi-gln-form-actions">
                    <label className="edi-gln-active">
                      <input
                        type="checkbox"
                        checked={glnForm.active}
                        onChange={(e) => handleGlnField('active', e.target.checked)}
                      />
                      {t('edi.active', 'Ativo')}
                    </label>
                    {glnEditingId && (
                      <button className="btn-secondary" onClick={cancelEditGln} disabled={glnSaving}>
                        {t('cancel', 'Cancelar')}
                      </button>
                    )}
                    <button className="btn-primary" onClick={handleSaveGln} disabled={glnSaving || !canEditGlnForm}>
                      {glnSaving
                        ? t('saving', 'A guardar...')
                        : glnEditingId ? t('save', 'Guardar') : <><FiPlus /> {t('add', 'Adicionar')}</>}
                    </button>
                  </div>
                </div>
              )}

              <DataTable
                columns={glnColumns}
                data={glns}
                loading={glnLoading}
                emptyMessage={t('edi.gln_none', 'Nenhum GLN registado para este cliente.')}
              />
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeGlnModal}>
                {t('close', 'Fechar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            id={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            requireConfirm={toast.requireConfirm}
            onConfirm={toast.onConfirm}
            onClose={removeToast}
          />
        ))}
      </div>
    </div>
  );
};

export default EdiClientConfig;